import {biErrorCodes} from './BIErrors'
import {Store} from 'redux'
import {pick} from 'lodash'

import {
  BITrackingServicesList,
  IBIEventObj,
  IExternalReportingOptions,
  IReportEnhancer,
  IReportingOptions,
  ITrackingService
} from 'powtoon-commons/biTracker/types'


const DEFAULT_REPORTING_OPTIONS: IReportingOptions = {
  sendSynchronously: false,
  trackingServices: [],
  preventDefaultTrackingServiceUsage: false
}

export class BIReporter {

  private readonly _logger: any
  private readonly _extraTrackingServices: Map<BITrackingServicesList, ITrackingService>
  private readonly _defaultTrackingServices: ITrackingService[]
  private _reportEnhancers: IReportEnhancer[]
  private _store: Store | null

  constructor(logger: any, defaultTrackingService?: ITrackingService) {
    this._logger = logger
    this._store = null
    this._extraTrackingServices = new Map<BITrackingServicesList, ITrackingService>()
    this._reportEnhancers = []
    this._defaultTrackingServices = defaultTrackingService ? [defaultTrackingService] : []
  }

  public setStore = (store: Store) => {
    this._store = store
  }

  public config = (params: IReportEnhancer[]) => {
    this._reportEnhancers = params
  }

  public addDefaultTrackingService = (trackingService: ITrackingService) => {
    this._defaultTrackingServices.push(trackingService)
  }

  public addRegularTrackingService = (trackingServiceId: BITrackingServicesList, trackingService: ITrackingService) => {
    this._extraTrackingServices.set(trackingServiceId, trackingService)
  }

  public reportEvent = (event: IBIEventObj) => {
    try {
      const state = this._store?.getState()
      const extraData = this._reportEnhancers.reduce((acc, reportEnhancer) => {
        const additionalExtraData = reportEnhancer(event, state) || {}
        return {...additionalExtraData, ...acc}
      }, event.extraData || {})

      const reportingOptions: IReportingOptions = {
        ...DEFAULT_REPORTING_OPTIONS,
        ...event.reportingOptions
      }

      let trackingServicesToReport: ITrackingService[] = []

      if (!reportingOptions.preventDefaultTrackingServiceUsage) {
        trackingServicesToReport = trackingServicesToReport.concat(this._defaultTrackingServices)
      }

      reportingOptions.trackingServices.forEach((trackingService) => {
        if (this._extraTrackingServices.has(trackingService)) {
          trackingServicesToReport.push(this._extraTrackingServices.get(trackingService) as ITrackingService)
          return
        }
        this.logError(new Error(`Requested tracking service ${trackingService} was not defined`))
      })

      const externalReportingOptions: IExternalReportingOptions = pick(reportingOptions, ['sendSynchronously'])


      trackingServicesToReport.forEach(trackingService =>
        trackingService.report({...event, extraData, reportingOptions: externalReportingOptions}))

    } catch (error) {
      this.logError(new Error('Error while reporting event in biReporter'),
        {errorCode: biErrorCodes.ReporterError, error, event})
    }
  }

  logError = (...args: any) => {
    try {
      this._logger.error(...args)
    } catch (error) {
      // do nothing
    }
  }
}
